//#region libraries
import React, { useEffect, useState } from "react";

// custom components
import { InformationContainer, Card, CustomCard } from "_atoms";
import { ForecastedBookingGraph } from "_organisms";

// bootstrap
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { selectDateToISO, selectSeasonalVarianceFilter } from "_redux/slices/prm-bookings/calendar";
import Button from "react-bootstrap/Button";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
  selectDailyAnalyticsIsLoading,
  selectAnalyticsForecastStats,
  selectDeparturesForecastStatus,
  selectArrivalsForecastStatus,
  resetForecastStatus
} from "_redux/slices/prm-bookings/bookings-count";
import { selectIata, selectUserDb } from "_redux/slices/main-screen/user";
import * as BookingAnalyticsService from "_services/bookingAnalytics-service";
import {
  getForecastNotifications,
  selectNotification
} from "_redux/slices/prm-bookings/booking-forecast";

// exterior libraries
import { format } from "date-fns";

// utils
import { Colors, Spacing } from "_styles";

// tremor librarues
import {
  ColGrid,
  Flex,
  Text,
  Bold
} from "@tremor/react";
import { DeltaBar } from "@tremor/react";

// icons
import { AiFillQuestionCircle } from "react-icons/ai";
import { selectAirlineSelectedAirport } from "_redux/slices/prm-bookings/bookings";
import { SeasonalVarianceToggle } from "_components/molecules";
import { selectSeasonalVariances } from "_redux/slices/prm-bookings/seasonal-variance";
//#endregion

//#region components
// const dataFormatter = (number: number) => {
//     return Intl.NumberFormat("us").format(number).toString();
// };

//#endregion

const ForecastAnalytics = (props) => {
  //#region redux
  const dispatch = useDispatch();
  const analyticsDate = useSelector(selectDateToISO); // start date of the week  
  const analyticsStats = useSelector(selectAnalyticsForecastStats);
  const dbUser = useSelector(selectUserDb);
  const isLoading = useSelector(selectDailyAnalyticsIsLoading);
  const departuresForecastStatus = useSelector(selectDeparturesForecastStatus);
  const arrivalsForecastStatus = useSelector(selectArrivalsForecastStatus);
  const iata = useSelector(selectIata);
  const wave1 = useSelector(selectNotification(1));
  const wave2 = useSelector(selectNotification(2));
  const wave3 = useSelector(selectNotification(3));
  const wave4 = useSelector(selectNotification(4));

  const seasonalVarianceFilter = useSelector(selectSeasonalVarianceFilter);

  const seasonalVariances = useSelector(selectSeasonalVariances);
  const [inboundForecast, setInboundForecast] = useState(0);
  const [outboundForecast, setOutboundForecast] = useState(0);
  const airport = useSelector(selectAirlineSelectedAirport);
  const [showWeather, setShowWeather] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [excludeNoShows, setExcludeNoShows] = useState(false);  // if true remove departure pax no shows from calendar count request
  //#endregion

  //#region useEffect
  useEffect(() => {
    dispatch(
      getForecastNotifications({
        date: analyticsDate,
        iata: dbUser.details.type == "AIRLINE" ? airport : iata,
        carrierCode:
          dbUser.details.type == "AIRLINE" ? dbUser.details.authority : "",
      })
    );
    dispatch(resetForecastStatus())
  }, []);

  useEffect(() => {
    dispatch(
      getForecastNotifications({
        date: analyticsDate,
        iata: dbUser.details.type == "AIRLINE" ? airport : iata,
        carrierCode:
          dbUser.details.type == "AIRLINE" ? dbUser.details.authority : "",
      })
    );
  }, [analyticsDate]);

  useEffect(() => {
    // check if date is today or in next 7 days
    let todayIso = format(new Date(), "yyyy-MM-dd");

    const date = new Date();
    date.setDate(date.getDate() + 7);

    if (todayIso == analyticsDate) {
      setShowWeather(true);
    }

    var date1 = new Date(date);
    var date2 = new Date(analyticsDate);
    var timeDiff = date1.getTime() - date2.getTime();
    var diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (diffDays < 8 && diffDays > -1) {
      setShowWeather(true);
    }
  }, []);

  // calculate seasonal variance
  useEffect(() => {

    let tempOutboundForecast = analyticsStats.outbound;
    let tempInboundForecast = analyticsStats.inbound;

    // check if any of the date ranges in the seasonal variance matches any dates in the forecast
    if (seasonalVariances && seasonalVariances.seasonalAdjustments && seasonalVarianceFilter) {

      // seasonal variances exist, check each one
      seasonalVariances.seasonalAdjustments.forEach((variance) => {

        // get date range of seasonal variance
        let startDate = new Date(variance.startDate);
        let endDate = new Date(variance.endDate);

        if (tempInboundForecast) {
          let forecastDate = new Date(analyticsDate);
          if (forecastDate >= startDate && forecastDate <= endDate) {

            // apply variance to forecast
            tempInboundForecast = analyticsStats.inbound + (analyticsStats.inbound * variance.multiplier);
          }

        }
        if (tempOutboundForecast) {
          let forecastDate = new Date(analyticsDate);

          if (forecastDate >= startDate && forecastDate <= endDate) {

            // apply variance to forecast
            tempOutboundForecast = analyticsStats.outbound + (analyticsStats.outbound * variance.multiplier);
          }

        }
      })
    }

    setInboundForecast(tempInboundForecast)
    setOutboundForecast(tempOutboundForecast)

  }, [seasonalVariances, analyticsStats.inbound, analyticsStats.outbound, seasonalVarianceFilter])

  // reset Forecast Status
  useEffect(() => {
    if (arrivalsForecastStatus == "succeeded" || departuresForecastStatus == "succeeded") {
      dispatch(resetForecastStatus())
    }
  }, [departuresForecastStatus, arrivalsForecastStatus])
  //#endregion

  //#region functions CSV

  let processCSV = (test_json) => {
    let string_list = [];
    string_list.push(
      "id, departureDate, dateCreated, Current/Previous, quarter"
    );
    for (let list2 in test_json) {
      for (let hour in test_json[list2].currentRunningAverage) {
        for (let object of test_json[list2].currentRunningAverage[hour]) {
          string_list.push(
            object.id +
            ", " +
            object.key[1] +
            ", " +
            object.key[3] +
            ", Current Today, " +
            list2
          );
        }
      }

      for (let hour in test_json[list2].previousRunningAverage) {
        for (let object of test_json[list2].previousRunningAverage[hour]) {
          string_list.push(
            object.id +
            ", " +
            object.key[1] +
            ", " +
            object.key[3] +
            ", Past Booking, " +
            list2
          );
        }
      }
    }
    return string_list.join("\n");
  };

  async function downloadCSVfile() {
    let params = {
      date: analyticsDate,
      iata: dbUser.details.type == "AIRLINE" ? airport : iata,
      carrierCode:
        dbUser.details.type == "AIRLINE" ? dbUser.details.authority : "",
      csv: true,
    };
    let csv = await BookingAnalyticsService.getForecastNotifications(params);
    csv = processCSV(csv);
    const element = document.createElement("a");
    const file = new Blob([csv], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "wave_analyser_export.csv";
    document.body.appendChild(element);
    element.click();
  }
  //#endregion

  return (
    <Row>

      <div style={{ display: 'flex' }}>
        <Form.Check
          onClick={(e) => { setExcludeNoShows(e.target.checked) }}
          type="switch"
          defaultChecked={excludeNoShows}
        />
        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Exclude Departure PAX No Shows</Form.Label>
        {/* seasonal variance toggle*/}
        <div style={{ marginLeft: Spacing.SCALE_22 }}>
          <SeasonalVarianceToggle />
        </div>
      </div>


      <ColGrid>
        {analyticsStats && (
          <>
            <ColGrid
              numColsMd={1}
              numColsLg={4}
              gapX="gap-x-6"
              gapY="gap-y-6"
              marginTop="mt-6"
            >
              <CustomCard
                title={"Departures Forecast"}
                data={outboundForecast}
                text={`${analyticsStats.outboundDiff
                  ? analyticsStats.outboundDiff.toFixed(1)
                  : 0
                  }%`}
                tooltip={"Percentage change in departures from the previous day"}
                isLoading={departuresForecastStatus == "loading"}
              />
              <CustomCard
                title={"Arrivals Forecast"}
                data={inboundForecast}
                text={`${analyticsStats.inboundDiff
                  ? analyticsStats.inboundDiff.toFixed(1)
                  : 0
                  }%`}
                isLoading={arrivalsForecastStatus == "loading"}
              />

              {
                <>
                  {wave1 && (
                    <Card>
                      <Flex>
                        <Text>
                          <Bold>Wave One Forecast</Bold>
                        </Text>
                        <AiFillQuestionCircle
                          size={22}
                          style={{
                            color: Colors.PRM_LIGHT_BLUE,
                            marginTop: -22,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setShowHelp(!showHelp);
                          }}
                        />
                      </Flex>
                      {/* {notificationStatus == "loading" ? (
                      <CustomSpinner />
                    ) : ( */}
                      <>
                        <Flex>
                          <Text>5-9am</Text>
                          <Text>
                            {wave1 && wave1.difference && wave1.percentage
                              ? wave1.difference.toFixed(1) +
                              " (" +
                              wave1.percentage.toFixed(1) +
                              "%)"
                              : ""}
                          </Text>
                        </Flex>
                        <DeltaBar
                          percentageValue={
                            wave1 && wave1.percentage ? wave1.percentage : ""
                          }
                          isIncreasePositive={false}
                          marginTop="mt-2"
                        />
                        <Flex marginTop="mt-2">
                          <Text>9-1pm</Text>
                          <Text>
                            {wave2 && wave2.difference && wave2.percentage
                              ? wave2.difference.toFixed(1) +
                              " (" +
                              wave2.percentage.toFixed(1) +
                              "%)"
                              : ""}
                          </Text>
                        </Flex>
                        <DeltaBar
                          percentageValue={
                            wave2 && wave2.percentage ? wave2.percentage : ""
                          }
                          isIncreasePositive={false}
                          marginTop="mt-2"
                        />
                      </>
                      {/* )} */}
                    </Card>
                  )}
                  {wave3 && (
                    <Card>
                      <Flex>
                        <Text>
                          <Bold>Wave Two Forecast</Bold>
                        </Text>
                      </Flex>
                      {/* {notificationStatus == "loading" ? (
                      <CustomSpinner />
                    ) : ( */}
                      <>
                        <Flex>
                          <Text>1-5pm</Text>
                          <Text>
                            {wave3 && wave3.difference && wave3.percentage
                              ? wave3.difference.toFixed(1) +
                              " (" +
                              wave3.percentage.toFixed(1) +
                              "%)"
                              : ""}
                          </Text>
                        </Flex>
                        <DeltaBar
                          percentageValue={
                            wave3 && wave3.percentage ? wave3.percentage : ""
                          }
                          isIncreasePositive={false}
                          marginTop="mt-2"
                        />
                        <Flex marginTop="mt-2">
                          <Text>5-9pm</Text>
                          <Text>
                            {wave4 && wave4.difference && wave4.percentage
                              ? wave4.difference.toFixed(1) +
                              " (" +
                              wave4.percentage.toFixed(1) +
                              "%)"
                              : ""}
                          </Text>
                        </Flex>
                        <DeltaBar
                          percentageValue={
                            wave4 && wave4.percentage ? wave4.percentage : ""
                          }
                          isIncreasePositive={false}
                          marginTop="mt-2"
                        />
                      </>
                      {/* )} */}
                    </Card>
                  )}
                </>
              }
            </ColGrid>
            {showHelp && (
              <InformationContainer
                title="Wave Forecasting"
                close={() => {
                  setShowHelp(false);
                }}
              >
                <>
                  <Text>
                    The wave forecaster can help predict the number of assistance
                    requests 36 hours in advance of the current time.
                  </Text>
                  <Text>
                    It splits up the day into six 4 hour time periods in the next
                    36 hours and shows how the current number of forecasted
                    requests within these time periods compares to the previous 7
                    days.
                  </Text>
                  <Text>
                    The percentages show what the current number of forecasted
                    requests is relative to the previous 7 days.
                  </Text>
                  <Text>
                    i.e "-26%" reads as 26% less than the last 7 day average for
                    this time period.
                  </Text>
                  <Button
                    variant={"outline-secondary"}
                    onClick={downloadCSVfile}
                    loading={true}
                    style={{ width: 150, marginLeft: 15 }}
                  >
                    Export
                  </Button>
                </>
              </InformationContainer>
            )}
          </>
        )}
        {!isLoading && <ForecastedBookingGraph />}
      </ColGrid>

    </Row>
  );
};

export default ForecastAnalytics;
